import { Paper, Skeleton, Grid } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { normalizeLevel } from "src/components/visibility";
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";
import DataGridPerformanceTableBrandOverview from "src/components/data-grid/data-grid-performance-split-brand-overview";
import { useGetPerformanceSplitQuery } from "src/context/api/brandOverview/api";
import { useGetVisibilityKpisQuery } from "src/context/api/competitorsMonitoring/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { fDate } from "src/utils/format-time";
import { setDatesForcedByActivation, setFilterDate, setFilterDateCompare } from "src/context/reducers/filter-settings/filterSlice";
import { useTranslation } from "react-i18next";
import BankingWidgetSummary from "./banking-widget-summary";
import { useGetSinceActivationQuery, useGetStatsQuery } from "src/context/api/businessOverview/api";
import { differenceInCalendarDays, parseISO, subDays } from "date-fns";


export default function BrandOverview() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const context = useContext(VisibilityContext);
    const theme = useTheme();
    const dispatch = useDispatch();
    const [sinceActivate, setSinceActivate]: any = useState(null);
    const disableSinceActivation = false;
    const { t, i18n } = useTranslation();

    const { data: sinceActivationData } = useGetSinceActivationQuery(
        { workspaceId: workspace?.id },
        { skip: !workspace?.id }
    );

    const hasInitializedActivation = useRef(false);

    const setLast30Days = useCallback(() => {
        const todayMinus1 = subDays(new Date(), 1);
        const start = subDays(todayMinus1, 29);
        dispatch(
            setFilterDate({
                start: fDate(start, "yyyy-MM-dd"),
                end: fDate(todayMinus1, "yyyy-MM-dd"),
            })
        );
    }, [dispatch]);

    useEffect(() => {
        if (hasInitializedActivation.current) return;

        if (disableSinceActivation) {
            hasInitializedActivation.current = true;
            setLast30Days();
            setSinceActivate(false);
            dispatch(setDatesForcedByActivation(false));
            return;
        }

        if (!sinceActivationData) return;
        hasInitializedActivation.current = true;

        if (sinceActivationData?.first_entry_date) {
            const start = parseISO(sinceActivationData.first_entry_date);
            const end = subDays(new Date(), 1);

            dispatch(
                setFilterDate({
                    start: fDate(start, "yyyy-MM-dd"),
                    end: fDate(end, "yyyy-MM-dd"),
                    skipAlignment: true
                })
            );

            const diffDays = differenceInCalendarDays(end, start) + 1;
            const compareEnd = subDays(start, 1);
            const compareStart = subDays(compareEnd, diffDays - 1);

            dispatch(
                setFilterDateCompare({
                    compareStart: fDate(compareStart, "yyyy-MM-dd"),
                    compareEnd: fDate(compareEnd, "yyyy-MM-dd"),
                    skipAlignment: true
                })
            );
            dispatch(setDatesForcedByActivation(true));
            setSinceActivate(true);
        } else {
            setLast30Days();
            setSinceActivate(false);
            dispatch(setDatesForcedByActivation(false));
        }
    }, [sinceActivationData, disableSinceActivation, dispatch, setLast30Days]);

    const {
        data: statsData,
        error: statsError,
        isFetching: isFetchingStats,
    } = useGetStatsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            compare_end_date: filterStore?.compareEnd,
            compare: filterStore?.compare,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        { skip: sinceActivate === null }
    );

    const filteredStats: any = useMemo(() => {
        if (!statsData) return [];
        return [
            {
                title: t("dashboard.global.savings"),
                value: statsData?.bankingWidgetSummary?.savings_previous_month,
                compare: statsData?.bankingWidgetSummary?.savings_previous_month_compare,
                isPercent: false,
                isCurrency: true,
                invertedColor: false,
                color: "success",
                definition: t("definitions.savings"),
            },
            {
                title: t("dashboard.global.ad_cost_with_signal"),
                value: statsData?.bankingWidgetSummary?.ad_cost_with_cb,
                compare: statsData?.bankingWidgetSummary?.ad_cost_with_cb_compare,
                isPercent: false,
                color: "warning",
                isCurrency: true,
                invertedColor: true,
                definition: t("definitions.ad_cost_with_signal"),
            },
            {
                title: t("dashboard.global.ad_cost_without_signal"),
                value: statsData?.bankingWidgetSummary?.ad_cost_without_cb,
                compare: statsData?.bankingWidgetSummary?.ad_cost_without_cb_compare,
                isPercent: false,
                color: "error",
                isCurrency: true,
                invertedColor: true,
                definition: t("definitions.ad_cost_without_signal"),
            },
            {
                title: t("dashboard.brandTraffic.totalBrandTraffic"),
                value: statsData?.bankingWidgetSummary?.brand_traffic,
                compare: statsData?.bankingWidgetSummary?.brand_traffic_compare,
                isPercent: false,
                color: "secondary",
                isCurrency: false,
                invertedColor: false,
                definition: t("definitions.brand_traffic"),
            },
        ];
    }, [statsData, i18n.language]);


    const { data: performanceSplitData, isFetching: isFetchingPerformanceSplit } = useGetPerformanceSplitQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            bqTable: workspace?.bqTable,
            compare_end_date: filterStore?.compareEnd,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            split: "country",
            compare: filterStore?.compare,
        },
        { skip: !workspace?.id }
    );

    const skipQuery = context ? true : !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: standaloneVisibilityData } = useGetVisibilityKpisQuery(
        {
            startDate:
                filterStore.start !== undefined
                    ? fDate(filterStore.start, "yyyy-MM-dd")
                    : null,
            endDate:
                filterStore.end !== undefined
                    ? fDate(filterStore.end, "yyyy-MM-dd")
                    : null,
            compareStartDate:
                filterStore.compareStart !== undefined
                    ? fDate(filterStore.compareStart, "yyyy-MM-dd")
                    : null,
            compareEndDate:
                filterStore.compareEnd !== undefined
                    ? fDate(filterStore.compareEnd, "yyyy-MM-dd")
                    : null,
            timestep: filterStore.timestep,
            devices: filterStore.devices,
            countries: filterStore.countries,
            keywords: filterStore.keywords,
            platforms: filterStore?.platforms,
            workspaceId: workspace?.id,
        },
        { skip: skipQuery }
    );

    const visibilityData = context?.visibilityData || (Array.isArray(standaloneVisibilityData) ? standaloneVisibilityData : []);

    const optimizationOverview = useMemo(() => {
        if (!performanceSplitData?.performanceSplit) return [];
        return performanceSplitData.performanceSplit.map(
            (item1: any, index: number) => {
                const match = visibilityData?.find(
                    (item2: any) => item2.country_name === item1.split
                );

                const topCompetitors = Array.isArray(match?.top_5_competitors)
                    ? match.top_5_competitors
                        .map((c: any) => c?.domain_name)
                        .filter(Boolean)
                    : [];

                const topCompetitorsPaidRates: Record<string, number> = Array.isArray(
                    match?.top_5_competitors
                )
                    ? match.top_5_competitors.reduce(
                        (acc: Record<string, number>, c: any) => {
                            const domain = c?.domain_name;
                            const rate = c?.paid_presence_rate;
                            if (domain && typeof rate === "number") acc[domain] = rate;
                            return acc;
                        },
                        {}
                    )
                    : {};

                return {
                    id: item1?.split ?? index,
                    ...item1,
                    ...match,
                    competitionEvolution: match?.level ? normalizeLevel(match.level) : null,
                    levelRaw: match?.level,
                    brandDomain: workspace?.url,
                    firstCompetitorDomain:
                        Array.isArray(match?.top_5_competitors) &&
                            match.top_5_competitors[0]?.domain_name
                            ? match.top_5_competitors[0].domain_name
                            : undefined,
                    isDemoBrand: workspace?.name?.includes("Demo"),
                    topCompetitors,
                    topCompetitorsPaidRates,
                    blendedPresenceRate: match?.blended_presence_rate ?? null,
                    paidPresenceRate: match?.client_paid_presence_rate ?? null,
                    firstCompetitorPaidPresenceRate:
                        match?.first_compet_paid_presence_rate ?? null,
                    presenceRateData: {
                        organicOnly: match?.organic_only ?? 0,
                        soloBidder: match?.solo_bidder ?? 0,
                        topAdPosition: match?.top_ad_position ?? 0,
                    },
                };
            }
        );
    }, [performanceSplitData, visibilityData, workspace?.url, workspace?.name]);

    return (
        <Box px={'24px'}>
            <Grid container spacing={2}>
                {(isFetchingStats || (!statsData && !statsError)
                    ? [1, 2, 3, 4]
                    : filteredStats
                ).map((stat: any, index: any) => (
                    <Grid item key={index} xs={6} md={4} lg={3}>
                        <BankingWidgetSummary
                            isFetching={isFetchingStats || (!statsData && !statsError)}
                            title={stat.title}
                            compare={stat.compare}
                            definition={stat?.definition}
                            color={stat?.color}
                            invertedColor={stat?.invertedColor}
                            isPercent={stat.isPercent}
                            isCurrency={stat?.isCurrency}
                            total={stat.value}
                        />
                    </Grid>
                ))}
            </Grid>

            <Box pt={2}>
                <Paper
                    sx={{
                        border: theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(145, 158, 171, 0.2)",
                    }}
                >

                    {isFetchingPerformanceSplit || !performanceSplitData ? (
                        <Skeleton
                            variant="rectangular"
                            height={400}
                            width="100%"
                            animation="wave"
                        />
                    ) : (
                        performanceSplitData?.performanceSplit?.some(
                            (item: any) => item.split
                        ) && (
                            <DataGridPerformanceTableBrandOverview
                                data={optimizationOverview}
                                compare={filterStore?.compare}
                            />
                        )
                    )}
                </Paper>
            </Box>
        </Box>
    );
}